/**
 * Dashboard admin avec KPIs globaux
 */

import { useAdminStats } from '@/hooks/admin/useAdminStats';
import { AdminKPICard } from '@/components/admin/AdminKPICard';
import {
  DollarSign,
  Users,
  Building2,
  HardHat,
  TrendingUp,
  Activity
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export default function AdminDashboard() {
  const { stats, loading, error } = useAdminStats();

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">Dashboard</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">Erreur : {error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Vue d'ensemble</h1>
        <p className="text-muted-foreground">Statistiques globales de la plateforme B8ild</p>
      </div>

      {/* KPIs principaux */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AdminKPICard
          title="MRR"
          value={`${stats?.mrr?.toLocaleString('fr-FR') ?? 0} €`}
          icon={DollarSign}
          subtitle="Revenus mensuels recurrents"
          variant="primary"
        />
        <AdminKPICard
          title="Utilisateurs"
          value={stats?.totalUsers ?? 0}
          icon={Users}
          subtitle="Comptes crees"
          href="/admin/users"
          actionHint="Voir les utilisateurs"
        />
        <AdminKPICard
          title="Entreprises"
          value={stats?.totalEntreprises ?? 0}
          icon={Building2}
          subtitle="Entreprises actives"
          href="/admin/entreprises"
          actionHint="Voir les entreprises"
        />
        <AdminKPICard
          title="Chantiers"
          value={stats?.totalChantiers ?? 0}
          icon={HardHat}
          subtitle="Chantiers ce mois"
        />
        <AdminKPICard
          title="Taux d'activation"
          value={`${stats?.activationRate ?? 0}%`}
          icon={Activity}
          subtitle="Users avec 1+ chantier"
        />
        <AdminKPICard
          title="ARR"
          value={`${((stats?.mrr ?? 0) * 12).toLocaleString('fr-FR')} €`}
          icon={TrendingUp}
          subtitle="Revenus annuels"
          variant="success"
        />
      </div>

      {/* Alertes recentes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-amber-500" />
            Alertes recentes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.alerts && stats.alerts.length > 0 ? (
            <ul className="space-y-2">
              {stats.alerts.map((alert, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  <span className={cn(
                    'w-2 h-2 rounded-full',
                    alert.type === 'error' ? 'bg-destructive' : 'bg-amber-500'
                  )} />
                  {alert.message}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted-foreground text-sm">Aucune alerte</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
